import { AppHTTPService } from '@/api/http-service'
import { useCallback, useEffect, useState } from 'react'
import { PrepareChatConfig, ChatItem } from './types'

/**
 * Helper function to ensure unique chat items
 */
function addUniqueChatItems(
  existingItems: ChatItem[],
  newItems: ChatItem | ChatItem[]
): ChatItem[] {
  const itemsToAdd = Array.isArray(newItems) ? newItems : [newItems]
  const existingIds = new Set(existingItems.map((item) => item.id))

  // Filter out items that already exist
  const uniqueNewItems = itemsToAdd.filter((item) => !existingIds.has(item.id))

  if (uniqueNewItems.length === 0) {
    return existingItems
  }

  return [...existingItems, ...uniqueNewItems]
}

/**
 * Hook for managing chat messages
 */
function useChatMessages(
  threadId: string | null,
  config?: PrepareChatConfig,
  showAssistantMessages: boolean = false
) {
  const [chatItems, setChatItems] = useState<ChatItem[]>([])
  const [optionsDisplayed, setOptionsDisplayed] = useState(false)
  const [optionsDisabled, setOptionsDisabled] = useState(false)
  const [statusText, setStatusText] = useState('Ready for action.')

  /**
   * Adds a new chat item ensuring uniqueness
   */
  const addChatItem = useCallback((newItem: ChatItem | ChatItem[]) => {
    setChatItems((prev) => addUniqueChatItems(prev, newItem))
  }, [])

  /**
   * Removes a chat item by ID
   */
  const removeChatItem = useCallback((id: string) => {
    setChatItems((prev) =>
      prev.filter(
        (item) =>
          !item.id.startsWith(id) &&
          item.id !== `${id}-options` &&
          item.id !== `${id}-assessment`
      )
    )
  }, [])

  /**
   * Updates an existing chat item by ID
   */
  const updateChatItem = useCallback(
    (id: string, updates: Partial<ChatItem>) => {
      setChatItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
      )
    },
    []
  )

  /**
   * Handles sending a message to the thread
   */
  const sendMessageToThread = useCallback(
    async (threadId: string, content: string, role: string = 'user') => {
      try {
        const response = await AppHTTPService.client.messages.create(threadId, {
          content: { text: content },
          type: 'text',
          role: role.toLowerCase(),
          metadata: {
            source: 'useChatMessages',
          },
        })
        return response.data
      } catch (error) {
        console.error('Error sending message to thread:', error)
        throw error
      }
    },
    [config?.threadId]
  )

  /**
   * Loads existing messages for a thread
   */
  const loadExistingMessages = useCallback(
    async (threadId: string) => {
      if (!threadId) return

      try {
        const response = await AppHTTPService.client.messages.list(threadId)

        if (response.data && Array.isArray(response.data)) {
          const messages = response.data
            .flatMap((message) => {
              // filter out assistant messages
              if (!showAssistantMessages && message.role === 'assistant') {
                return []
              }
              if (message.type) {
                switch (message.type) {
                  case 'text':
                    return [
                      {
                        id: message.id,
                        content: message.content || '',
                        role: message.role.toLowerCase(),
                        timestamp: new Date(message.createdAt).getTime(),
                        type: 'message',
                      },
                    ]
                  case 'tool_result':
                    if (message.content?.result) {
                      const { options, assessment, sheetId } =
                        message.content.result
                      if (options && assessment) {
                        return [
                          {
                            id: message.id + '-message',
                            content: assessment || '',
                            role: 'assistant',
                            timestamp: new Date(message.createdAt).getTime(),
                            type: 'message',
                          },
                          {
                            id: message.id + '-options',
                            content: message.content || '',
                            options: options.map((option: any) => ({
                              id: option.id,
                              label: option.title,
                              description: option.description,
                              action: option.action,
                              confidence: option.confidence,
                            })),
                            sheetId: sheetId, // Add sheetId to the options message
                            role: message.role.toLowerCase(),
                            timestamp: new Date(message.createdAt).getTime(),
                            type: 'options',
                          },
                        ]
                      } else {
                        if (message.content.toolName === 'AnalyzeDataTool') {
                          return [
                            {
                              id: message.id,
                              content: message.content.result || '',
                              role: 'assistant',
                              timestamp: new Date(message.createdAt).getTime(),
                              type: 'message',
                            },
                          ]
                        }
                      }
                    }
                    return []
                  case 'status':
                    return [
                      {
                        id: message.id,
                        content: message.content || '',
                        role: message.role.toLowerCase(),
                        timestamp: new Date(message.createdAt).getTime(),
                        type: 'status',
                      },
                    ]
                  default:
                    return []
                }
              } else {
                return message.content
                  ? [
                      {
                        id: message.id,
                        content: message.content || '',
                        role: message.role.toLowerCase(),
                        timestamp: new Date(message.createdAt).getTime(),
                        type: 'message',
                      },
                    ]
                  : []
              }
            })
            .filter(Boolean)

          setChatItems((prev) =>
            addUniqueChatItems(prev, messages as ChatItem[])
          )
        }
      } catch (error) {
        console.error('Error loading thread messages:', error)
      }
    },
    [config?.threadId]
  )

  // Load existing messages when threadId changes
  useEffect(() => {
    if (threadId) {
      loadExistingMessages(threadId)
    }
  }, [threadId, loadExistingMessages])

  return {
    chatItems: chatItems.sort((a, b) => a.timestamp - b.timestamp),
    addChatItem,
    updateChatItem,
    sendMessageToThread,
    optionsDisplayed,
    setOptionsDisplayed,
    optionsDisabled,
    setOptionsDisabled,
    statusText,
    setStatusText,
    removeChatItem,
  }
}

export { useChatMessages }
